import { parse, ParsedQuery, stringify } from 'query-string';
import { identity, pickBy } from 'lodash-es';

import { DEFAULT_OFFSET, DEFAULT_PER_PAGE_COUNT } from '@/services/constants';

import dayjs from '../dayjs';

/**
 * Function will create new url with added param.
 * @param toAdd param in {key: value} schema to add to url.
 */
export const addParamToUrl = (toAdd: Record<string, any>): string => {
    const legacyHash = location.hash.split('?')?.[1];
    const currentQuery = parse(legacyHash || location.search, { arrayFormat: 'comma', parseNumbers: true });
    const query = {
        ...currentQuery,
        ...toAdd,
    };
    const updatedQuery = pickBy(query, identity);

    return stringify(updatedQuery, { arrayFormat: 'comma' });
};

/**
 * Adding one day, and setting it on the T00:00:00
 * so all data will be retrived from given date.
 * @example input date -  2021-01-01T12:31:12Z
 * @results output date -  2021-01-02T00:00:00Z
 */
export const setAtEndOfDay = (date: string): string => dayjs.utc(date).add(1, 'day').startOf('day').toJSON();

export const parseUrlParams = <T>(url: string, options?: Record<string, unknown>): ParsedQuery<string> & T => {
    const query = parse(url, { ...options, arrayFormat: 'comma' });
    if (query?.from && query?.to) {
        query.from = dayjs.utc(query?.from as string).toJSON();
        query.to = dayjs.utc(query?.to as string).toJSON();
    }

    return query as any;
};

export const calculateOffsetAndLimit = (activePage = 1, perPage?: number): { offset: number; limit: number } => {
    const itemsOnPage = perPage || DEFAULT_PER_PAGE_COUNT;
    const offset = itemsOnPage * activePage - itemsOnPage;
    const limit = itemsOnPage;

    return { offset, limit };
};

export function calculateOffset(activePage: number, perPage: number, itemsCount?: number): number {
    if (!activePage || !perPage) {
        return DEFAULT_OFFSET;
    }
    let offset = perPage * activePage - perPage;

    if (itemsCount && offset >= perPage) {
        const isLastPageEmpty = perPage * (activePage - 1) >= itemsCount;
        offset = isLastPageEmpty ? offset - perPage : offset;
    }
    return offset;
}

export const calculateActivePage = (offset: number, limit: number, itemsCount?: number): number => {
    let activePage = (offset + limit) / limit;
    if (itemsCount && activePage !== 1) {
        const isLastPageEmpty = limit * (activePage - 1) >= itemsCount;
        activePage = isLastPageEmpty ? activePage - 1 : activePage;
    }
    return activePage;
};
